import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, HeartHandshake, ShieldCheck, DollarSign } from "lucide-react";
import { stats } from "../mock";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1574757974346-45bae947d89a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHx0cnVjayUyMGRyaXZlcnxlbnwwfHx8fDE3ODk0OTMyOTV8MA&ixlib=rb-4.1.0&q=85";

const values = [
  {
    icon: DollarSign,
    title: "Safety Over Profit",
    text: "We price every class at just $119.95 (endorsements $59.95) because we believe safe, well-trained drivers matter more than high margins.",
  },
  {
    icon: Award,
    title: "Real Experience",
    text: "Our curriculum is built by CDL instructors with over 100 years of combined, real-world training experience.",
  },
  {
    icon: ShieldCheck,
    title: "Fully Compliant",
    text: "Every course meets 100% of FMCSA requirements and results are reported to the National Registry instantly.",
  },
  {
    icon: HeartHandshake,
    title: "People First",
    text: "An in-house support team of real people is ready to help you at every step of your journey.",
  },
];

function PageHero({ eyebrow, title, subtitle }) {
  return (
    <section className="relative pt-[76px] bg-[#F4F7FB] diag-lines">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <span className="eyebrow">{eyebrow}</span>
        <h1 className="font-display text-5xl md:text-6xl text-brand mt-3">{title}</h1>
        {subtitle && (
          <p className="text-slate-600 text-lg mt-4 max-w-2xl">{subtitle}</p>
        )}
      </div>
    </section>
  );
}

export default function About() {
  return (
    <main>
      <PageHero
        eyebrow="Our Story"
        title="About CDL Academy"
        subtitle="A better, more powerful — and more affordable — way to earn your commercial driving credentials."
      />

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative rounded-3xl overflow-hidden soft-shadow">
            <img src={ABOUT_IMG} alt="Professional truck driver" className="w-full h-full object-cover" />
          </div>
          <div>
            <span className="eyebrow">Built By Instructors</span>
            <h2 className="font-display text-4xl text-brand mt-3 mb-5">
              A Mission To Train Better Drivers
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              CDL Academy was founded on a simple idea: high-quality Entry-Level
              Driver Training shouldn’t cost a fortune. While other low-cost
              providers cut corners, we include every topic mandated by the FMCSA.
            </p>
            <p className="text-slate-600 leading-relaxed mb-8">
              Our courses are developed and designed by a team of CDL instructors
              with real-world training experience — over 100 years of it combined.
              The result is training that actually prepares you for the road and
              the test.
            </p>
            <Link to="/courses" className="btn-brand rounded-md px-7 py-3.5 inline-flex items-center gap-2">
              Explore Courses <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#16365C]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-display text-4xl lg:text-5xl text-white">{s.value}</div>
              <div className="font-head font-medium text-xs uppercase tracking-widest text-white/70 mt-1">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#F4F7FB]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="eyebrow">What Drives Us</span>
            <h2 className="font-display text-4xl md:text-5xl text-brand mt-3">
              Our Core Values
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div key={v.title} className="card-lift bg-white border border-slate-200 rounded-2xl p-7 soft-shadow">
                <div className="w-12 h-12 rounded-lg bg-[#16365C]/8 flex items-center justify-center mb-5">
                  <v.icon className="w-6 h-6 text-steel" />
                </div>
                <h3 className="font-head font-semibold text-slate-900 text-lg mb-2">{v.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
